import type { ReactNode } from "react";
import { LOCAL_PEOPLE, forget } from "@/lib/tabSession";
import { Logo } from "@/components/brand";

import { Link } from "@tanstack/react-router";

import { Button, Popover } from "@/components/ui";

import { useSession, useSignOut } from "./useSession";

import "./landing.css";
import "./marketing.css";
import { apiBaseUrl } from "../../lib/env";

/** Local rung only: `?as=` names who to be, so one browser can hold several people. */
/** `streamer` is the account the arena seeds give every space and activity to. */
const OWNS_THE_SPACES = "streamer";

/** The header, footer and page frame every marketing page shares. */
export function MarketingShell({ nav, children }: { nav?: ReactNode; children: ReactNode }) {
  const { data: account } = useSession();
  const signedIn = account != null;
  const signOut = useSignOut();
  return (
    <div className="mk">
      <header className="mk-head">
        <div className="mk-wrap mk-head-in">
          <Link to="/" className="mk-mark">
            <Logo size={24} />
          </Link>
          <nav className="mk-nav" aria-label="Main">
            {nav ?? (
              <>
                <a href="/#tracker">Eye tracker</a>
                <Link to="/activities">Activities</Link>
                <Link to="/publish">Build an activity</Link>
                <Link to="/pricing">Pricing</Link>
              </>
            )}
          </nav>
          {signedIn ? (
            <div className="mk-head-account">
              <Link to="/console">
                <Button size="sm">Open the console</Button>
              </Link>
              <Popover
                menuLabel="Account"
                align="end"
                minWidth={200}
                triggerClassName="mk-head-who"
                label={<span>{account.handle}</span>}
              >
                {(close) => (
                  <button
                    type="button"
                    role="menuitem"
                    className="al-popover-item al-popover-item-danger"
                    onClick={() => {
                      close();
                      signOut.mutate(undefined, {
                        onSuccess: () => {
                          window.location.assign("/");
                        },
                      });
                    }}
                  >
                    Sign out
                  </button>
                )}
              </Popover>
            </div>
          ) : (
            <Link to="/sign-in">
              <Button size="sm" variant="secondary">
                Sign in
              </Button>
            </Link>
          )}
        </div>
      </header>

      <main className="mk-wrap">{children}</main>

      <footer className="mk-foot">
        <div className="mk-wrap">
          <div className="mk-foot-cols">
            <div>
              <h4>Product</h4>
              <ul>
                <li>
                  <Link to="/pricing">Pricing</Link>
                </li>
                <li>
                  <Link to="/activities">Activities</Link>
                </li>
                <li>
                  <a href="/#tracker">Eye tracker</a>
                </li>
              </ul>
            </div>
            <div>
              <h4>Build</h4>
              <ul>
                <li>
                  <Link to="/publish">Build an activity</Link>
                </li>
              </ul>
            </div>
            <div>
              <h4>Account</h4>
              <ul>
                <li>
                  <Link to="/sign-in">Sign in</Link>
                </li>
                <li>
                  <Link to="/console">Console</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mk-dev">
            <div className="mk-dev-row">
              <span className="mk-dev-tag">development</span>
              <Link to="/console">console</Link>
              <Link to="/kit">design kit</Link>
              <a href="/agent/status">agent status</a>
              <a href="/agent/pairing">agent pairing</a>
              <a href="/agent/calibration">agent calibration</a>
              <a href="/agent/local-settings">agent settings</a>
            </div>
            <div className="mk-dev-row">
              <span className="mk-dev-tag">sign in as</span>
              {LOCAL_PEOPLE.map((who) => (
                <a
                  key={who}
                  href={`${apiBaseUrl}/v1/auth/twitch/start?as=${who}`}
                  onClick={forget}
                >
                  {who === OWNS_THE_SPACES ? `${who}, owns the spaces` : who}
                </a>
              ))}
              <span className="mk-dev-who">{account ? account.handle : "signed out"}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
